import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { accountService } from '../../services/api/accountService';
import { useAuth } from '../../context/AuthContext';
import AuthCard from '../common/AuthCard/AuthCard';
import {
  BodyText,
  LoadingRow,
  StatusBadge,
  SubmitButton,
} from '../common/AuthCard/AuthCard.styled';
import NkySpinner from '../common/NkySpinner/NkySpinner';

/** Title, badge and next step for each verify-email API status (`null` = link missing). */
const getOutcome = (status) => {
  switch (status) {
    case 201:
      return { title: 'Email confirmed', tone: 'success', Icon: CheckCircleRoundedIcon, action: 'Return home', path: '/' };
    case 409:
      return { title: 'Already confirmed', tone: 'success', Icon: CheckCircleRoundedIcon, action: 'Return home', path: '/' };
    case 401:
      return { title: 'This link has expired', tone: 'error', Icon: ErrorOutlineRoundedIcon, action: 'Sign in', path: '/login' };
    case 404:
      return { title: 'No account found', tone: 'error', Icon: ErrorOutlineRoundedIcon, action: 'Sign up', path: '/sign-up' };
    case 500:
      return { title: 'Something went wrong', tone: 'error', Icon: ErrorOutlineRoundedIcon, action: 'Return home', path: '/' };
    case null:
      return { title: 'This link is not valid', tone: 'error', Icon: LinkOffRoundedIcon, action: 'Sign in', path: '/login' };
    default:
      return { title: 'Email verification', tone: 'neutral', Icon: MarkEmailReadRoundedIcon, action: null, path: '/' };
  }
};

const EmailVerificationPage = () => {
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailParam = searchParams.get('email');
  const email = emailParam ? decodeURIComponent(emailParam).trim().toLowerCase() : null;
  const code = searchParams.get('code');

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!email || !code) {
      setMessage(
        'This page needs a valid verification link. Open the link from your email, or sign in to request a new one.'
      );
      setLoading(false);
      return;
    }

    const syncSessionAfterVerification = async (statusCode) => {
      if (statusCode === 201 || statusCode === 409) {
        await refreshUser();
      }
    };

    accountService.verifyEmail(email, code)
      .then(async (statusCode) => {
        setStatus(statusCode);
        if (statusCode === 201) {
          setMessage("Thanks for confirming your email. You're ready to go.");
        }
        await syncSessionAfterVerification(statusCode);
        setLoading(false);
      })
      .catch(async (errorStatus) => {
        setStatus(errorStatus);
        switch (errorStatus) {
          case 401:
            setMessage('This verification link has expired. Please log in to request a new one.');
            break;
          case 409:
            setMessage("Looks like your email was already confirmed. You're good to go.");
            await syncSessionAfterVerification(errorStatus);
            break;
          case 404:
            setMessage('No account matched this email. Want to create a new one?');
            break;
          case 500:
            setMessage('Something went wrong on our end. Please try again later.');
            break;
          default:
            setMessage('Unexpected response. Please try again or contact support.');
        }
        setLoading(false);
      });
  }, [email, code, refreshUser]);

  if (loading) {
    return (
      <AuthCard title="Confirming your email">
        <LoadingRow role="status">
          <NkySpinner size="sm" />
          Verifying your email…
        </LoadingRow>
      </AuthCard>
    );
  }

  const { title, tone, Icon, action, path } = getOutcome(email && code ? status : null);

  return (
    <AuthCard
      title={title}
      badge={
        <StatusBadge $tone={tone} aria-hidden>
          <Icon />
        </StatusBadge>
      }
    >
      <BodyText>{message}</BodyText>
      {action && (
        <SubmitButton type="button" onClick={() => navigate(path)}>
          {action}
        </SubmitButton>
      )}
    </AuthCard>
  );
};

export default EmailVerificationPage;
